import { Link } from "react-router-dom";
import {
  Users,
  DollarSign,
  ClipboardCheck,
  Bot,
  Brain,
  ArrowRight,
  ArrowUpRight,
} from "lucide-react";
import DcPage from "@/components/landing/dcv2/DcPage";
import PortalCarousel from "@/components/landing/PortalCarousel";

/**
 * Solutions — the platform's reference page.
 *
 * Rebuilt to sit on the homepage's story rather than beside it. The page used
 * to present six co-equal cards under its own taxonomy (EMS / Accounting /
 * Productivity / AI / SaaS / Corporate Memory), which read as a product list
 * from a different company than the one the homepage describes. It is now the
 * homepage's three pillars — People & HR, Finance, Work & Operations, matching
 * BentoGrid exactly, colours included — with the two cross-cutting layers
 * (HiveMind, Corporate Memory) shown as what they are: one layer running
 * through all three, not a fourth and fifth sibling. The platform architecture
 * sits underneath as a foundation, where it belongs.
 *
 * Every capability name is the copy this page already shipped. Nothing here
 * claims a number, an outcome, a timeline, or a customer.
 *
 * "Our Process — Discover / Design / Deliver" is gone rather than reworded:
 * "assess opportunities, risks, and ROI" is an agency's engagement model, and
 * no honest SaaS replacement for it exists that would not be inventing an
 * onboarding promise.
 *
 * Composition deliberately changes four times — a two-column hero with a real
 * index, wide pillar bands with a sticky identity column, an asymmetric layer
 * section built around a diagram, and a single-row plinth for the foundation —
 * so the page is never a grid of same-size icon-heading-text cards.
 *
 * ─── Surface ──────────────────────────────────────────────────────────────
 * The page now wraps in <DcPage>, the v2 canvas shell shared with the homepage
 * and the About Us destinations, which supplies the nav, the footer, the two
 * families, the eleven custom properties, the theme toggle and the scroll
 * runtime. Every section below is authored in that language: inline style
 * objects rather than Tailwind, `var(--paper2)` / `rgba(var(--ink-rgb),…)`
 * rather than the `--landing-*` tokens, and Manrope throughout. Eyebrows,
 * labels and numerals ran in JetBrains Mono until the site consolidated to a
 * single family on 2026-09-18; they keep their tracking and case, not a face
 * of their own.
 *
 * Consequences worth naming, because each replaced something deliberate:
 *
 * - Motion. The framer-motion work — the word-by-word headline mask, the
 *   per-row scroll-sliced capability reveals, the scroll-drawn diagram
 *   connectors and their travelling dashes — is replaced by the canvas's one
 *   vocabulary: `data-rv` fades a block up with an 80ms sibling stagger and
 *   `data-plx` parallaxes a decorative bloom. There are no observers, scroll
 *   listeners or springs left on this route, and `prefers-reduced-motion` is
 *   handled once in the runtime rather than branched on here.
 * - The diagram. It is now drawn once, statically, in canvas tokens. It still
 *   carries the three pillar accents so it reads as "those three" without a
 *   legend, which was the only job the animation was doing that the picture
 *   was not already doing by itself.
 * - AmbientField and Spotlight. Both belonged to the previous shell's gradient
 *   surface. The canvas's equivalent is the parallaxed cyan bloom, so the
 *   bands carry those instead of two gradient layers each.
 * - The pillar accents (#60A5FA / #34D399 / #FBBF24) and the layer accent
 *   (#A78BFA) survive as literals. They are the only hardcoded colours here
 *   and they are theme-independent by design — the canvas does the same thing
 *   with the five status-dot hues in its capability grid.
 *
 * NOT restyled: <PortalCarousel />. It lives in its own shared file outside
 * this route and still speaks the `--landing-*` vocabulary.
 */

/* ─────────────────────── Canvas tokens ─────────────────────── */

const SANS = "'Manrope', sans-serif";
const MONO = "'Manrope', sans-serif";
const HAIRLINE = "1px solid rgba(var(--ink-rgb),.13)";
const RULE = "1px solid rgba(var(--ink-rgb),.2)";

/* Vertical padding only. The side gutter is `.site-gutter` on each section, so
   it can narrow on a phone (global.css, T101026002). */
const SECTION: React.CSSProperties = {
  paddingTop: 118,
  paddingBottom: 118,
  position: "relative",
  overflow: "clip",
};

const INNER: React.CSSProperties = {
  maxWidth: 1240,
  margin: "0 auto",
  position: "relative",
};

const H2: React.CSSProperties = {
  margin: 0,
  fontFamily: SANS,
  fontWeight: 600,
  fontSize: "clamp(28px, 3.4vw, 46px)",
  letterSpacing: "-0.02em",
  lineHeight: 1.14,
};

const H3: React.CSSProperties = {
  margin: 0,
  fontFamily: SANS,
  fontWeight: 600,
  fontSize: 18,
  letterSpacing: "-0.01em",
};

const BODY: React.CSSProperties = {
  margin: 0,
  fontSize: 16,
  color: "rgba(var(--ink-rgb),.66)",
};

const CARD_BODY: React.CSSProperties = {
  margin: 0,
  fontSize: 14.5,
  color: "rgba(var(--ink-rgb),.66)",
};

/**
 * The eyebrow slot, carrying the canvas's mark and nothing else. The homepage
 * pairs the glyph with a label; this page has no label to pair it with and
 * inventing one would be new copy, so the mark stands alone as the rhythm mark
 * above each heading.
 */
function Mark() {
  return (
    <div
      aria-hidden="true"
      style={{
        fontFamily: MONO,
        fontSize: 11.5,
        letterSpacing: ".16em",
        color: "var(--link)",
        marginBottom: 18,
      }}
    >
      <span className="dc-mark" aria-hidden="true" />
    </div>
  );
}

/** A decorative cyan bloom, parallaxed by the shared runtime. */
/* Accents arrive as hex so they can be tinted at several opacities. */
function hexToRgb(hex: string): string {
  const h = hex.replace("#", "");
  return `${parseInt(h.slice(0, 2), 16)}, ${parseInt(h.slice(2, 4), 16)}, ${parseInt(h.slice(4, 6), 16)}`;
}

/* The canvas nav is fixed at 68px, so anchor targets need clearance. */
const SCROLL_MARGIN = "6rem";

type Pillar = {
  id: string;
  icon: typeof Users;
  label: string;
  /* The product name this pillar ships under. Kept, because dropping it would
     lose real information — but placed under the heading, never above it. */
  product: string;
  tagline: string;
  color: string;
  items: string[];
};

const PILLARS: Pillar[] = [
  {
    id: "people",
    icon: Users,
    label: "People & HR",
    product: "Employee Management System",
    tagline: "End-to-end workforce management, from hire to retire.",
    color: "#60A5FA",
    items: [
      "Core HR Management",
      "Talent Acquisition",
      "Performance & Talent Management",
      "Employee Self-Service Portal",
      "Manager Self-Service Portal",
    ],
  },
  {
    id: "finance",
    icon: DollarSign,
    label: "Finance",
    product: "Accounting & Financial Solutions",
    tagline: "Modern financial operations with real-time visibility.",
    color: "#34D399",
    items: [
      "Accounting Management",
      "Expense Management",
      "Inventory Management",
      "Customer & Vendor Management",
      "Financial Analytics & Insights",
      "Financial Reporting & Statements",
    ],
  },
  {
    id: "operations",
    icon: ClipboardCheck,
    label: "Work & Operations",
    product: "Productivity Solutions",
    tagline:
      "Connected collaboration, automated workflows, and built-in document creation for the everyday work in between.",
    color: "#FBBF24",
    items: [
      "Task Management",
      "Team Communication Hub",
      "Document Builder",
      "Workflow Automation",
      "Work Management & Operations",
      "Productivity & Performance Analytics",
    ],
  },
];

const LAYERS = [
  {
    icon: Bot,
    label: "HiveMind",
    product: "AI agents that automate routine business work",
    tagline: "Intelligent automation powered by enterprise-grade AI.",
    items: [
      "Conversational AI Assistants",
      "Role-based AI Agents",
      "Embedded AI & LLM Integration",
      "Predictive Analytics & Forecasting",
    ],
  },
  {
    icon: Brain,
    label: "Corporate Memory",
    product: "Context and knowledge that carries across the platform",
    tagline: "Turn everyday operations into organizational knowledge.",
    items: [
      "Centralized Corporate Memory across all products",
      "Cross-system insights (HR + Finance + Tasks + Operations)",
      "Predictive recommendations and decision support",
      "Organization-wide search and knowledge retrieval",
      "Continuous learning from business activity",
    ],
  },
];

const FOUNDATION = [
  "Multi-Tenant SaaS Architecture",
  "Cloud Infrastructure Management",
  "API Gateway & Integrations",
  "Scalable Microservices Architecture",
  "Real-Time Data Synchronization",
];

const LAYER_ACCENT = "#A78BFA";

/* Index rows, in page order. Descriptors are short enough to scan, not a
   second copy of the taglines below. */
const INDEX = [
  { id: "people", label: "People & HR", note: "The full employee lifecycle" },
  { id: "finance", label: "Finance", note: "Financial operations, automated" },
  { id: "operations", label: "Work & Operations", note: "Keep work moving" },
  { id: "intelligence", label: "HiveMind & Corporate Memory", note: "The layer across all three" },
  { id: "foundation", label: "Platform Foundation", note: "What it all runs on" },
];

/* ─────────────────────────── Hero ─────────────────────────── */

const HEADLINE = "The platform, capability by capability.";

/**
 * In-page jump.
 *
 * The browser's own fragment navigation is better than anything re-implemented
 * here — it scrolls, it moves focus, it updates the URL — so it is left alone
 * for everyone except readers who asked for less motion. `scroll-behavior:
 * smooth` is set globally on <html> and is not withdrawn under the media
 * query, so for those readers the jump is taken over and forced to "instant",
 * with focus moved by hand to keep the keyboard path identical.
 *
 * The preference is read here rather than through a hook because the route no
 * longer subscribes to it for anything else: the canvas runtime owns motion,
 * and a click handler can ask the question at the moment it matters.
 */
function jumpInstantly(e: React.MouseEvent<HTMLAnchorElement>, id: string) {
  if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const el = document.getElementById(id);
  if (!el) return;
  e.preventDefault();
  el.scrollIntoView({ behavior: "instant", block: "start" });
  el.focus({ preventScroll: true });
}

function Hero() {
  return (
    <section
      className="site-gutter"
      style={{ ...SECTION, paddingTop: 104, paddingBottom: 92 }}
      aria-labelledby="solutions-heading"
    >
      <div
        style={{
          ...INNER,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
          gap: "56px 72px",
          alignItems: "end",
        }}
      >
        <div>
          <div data-rv="">
            <Mark />
            <h1
              id="solutions-heading"
              style={{
                margin: 0,
                fontFamily: SANS,
                fontWeight: 800,
                fontSize: "clamp(38px, 4.6vw, 64px)",
                lineHeight: 1.08,
                letterSpacing: "-0.025em",
              }}
            >
              {HEADLINE}
            </h1>
          </div>

          <p
            data-rv=""
            style={{
              margin: "24px 0 0",
              maxWidth: "60ch",
              fontSize: 17,
              color: "rgba(var(--ink-rgb),.7)",
            }}
          >
            HR, accounting, and everyday operations on one platform, with AI
            handling the routine work in between. This page lists what is in
            it.
          </p>

          <div
            data-rv=""
            style={{
              marginTop: 34,
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            <Link
              to="/contact"
              className="dc-h-btn-lg"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                background: "var(--btn)",
                color: "var(--btnfg)",
                padding: "15px 28px",
                borderRadius: 999,
                fontWeight: 600,
                fontSize: 15,
                transition: "transform .2s, box-shadow .2s",
              }}
            >
              Book a Demo
              <ArrowRight size={16} aria-hidden="true" />
            </Link>
            <Link
              to="/pricing"
              className="dc-h-ghost"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                border: "1px solid rgba(var(--ink-rgb),.3)",
                color: "var(--ink)",
                padding: "15px 28px",
                borderRadius: 999,
                fontWeight: 600,
                fontSize: 15,
                transition: "border-color .2s, background .2s",
              }}
            >
              See pricing
            </Link>
          </div>
        </div>

        <PageIndex />
      </div>
    </section>
  );
}

/**
 * The page's own contents. A reference page this long should say what is on it
 * before asking anyone to scroll, and every row is a real link — reachable by
 * tab, activated by Enter, never dependent on hover to be readable.
 */
function PageIndex() {
  return (
    <nav data-rv="" aria-label="On this page">
      <ul style={{ listStyle: "none", margin: 0, padding: 0, borderTop: RULE }}>
        {INDEX.map(({ id, label, note }) => (
          <li key={id} style={{ borderBottom: RULE }}>
            <a
              href={`#${id}`}
              onClick={(e) => jumpInstantly(e, id)}
              className="dc-h-menurow"
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 16,
                margin: "0 -10px",
                padding: "13px 10px",
                borderRadius: 10,
                border: "1px solid transparent",
                color: "var(--ink)",
                transition: "background .2s, border-color .2s, color .2s",
              }}
            >
              <span style={{ fontFamily: SANS, fontSize: 15.5, fontWeight: 600 }}>
                {label}
              </span>
              <span
                style={{
                  marginLeft: "auto",
                  fontFamily: MONO,
                  fontSize: 11.5,
                  color: "rgba(var(--ink-rgb),.62)",
                }}
              >
                {note}
              </span>
              <ArrowUpRight
                size={15}
                aria-hidden="true"
                style={{
                  flex: "none",
                  alignSelf: "center",
                  color: "rgba(var(--ink-rgb),.62)",
                }}
              />
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/* ─────────────── Convergence diagram (the focal moment) ─────────────── */

const DIAGRAM_NODES = [
  { label: "People & HR", cx: 54, color: "#60A5FA" },
  { label: "Finance", cx: 160, color: "#34D399" },
  { label: "Work & Ops", cx: 266, color: "#FBBF24" },
];

const TOP_Y = 26;
const NODE_H = 28;
const TOP_W = 98;

/** From each pillar into the agents, then from the agents into memory. */
const FLOWS = [
  { x1: 54, y1: 40, x2: 160, y2: 112 },
  { x1: 160, y1: 40, x2: 160, y2: 112 },
  { x1: 266, y1: 40, x2: 160, y2: 112 },
  { x1: 160, y1: 144, x2: 160, y2: 198 },
];

function DiagramNode({
  x,
  y,
  w,
  label,
  color,
  emphasis,
}: {
  x: number;
  y: number;
  w: number;
  label: string;
  color: string;
  emphasis?: boolean;
}) {
  return (
    <>
      <rect
        x={x - w / 2}
        y={y - NODE_H / 2}
        width={w}
        height={NODE_H}
        rx={8}
        fill={emphasis ? "var(--paper2)" : "var(--paper)"}
        /* The three pillar nodes carry the same accents as the bands above, so
           the diagram is legible as "those three" without a legend. */
        stroke={color}
        strokeOpacity={emphasis ? 1 : 0.55}
        strokeWidth={1}
      />
      <text
        x={x}
        y={y + 4}
        textAnchor="middle"
        fontFamily={MONO}
        fontSize={10.5}
        fontWeight={emphasis ? 500 : 400}
        fill={emphasis ? "var(--ink)" : "rgba(var(--ink-rgb),.66)"}
      >
        {label}
      </text>
    </>
  );
}

/**
 * The section's one authored moment: the three pillars above, the agents that
 * act on their work, and the memory that accumulates underneath. It draws the
 * claim the copy makes — one layer, not two more products — which is the whole
 * reason this section is not a third and fourth pillar band.
 *
 * It used to draw itself in on scroll, connector by connector, with a dash
 * travelling down each one. The canvas has one arrival — `data-rv` on the
 * block that holds it — so the picture is now static and the reveal is the
 * shared one.
 */
function ConvergenceDiagram() {
  return (
    <svg
      viewBox="0 0 320 230"
      role="presentation"
      aria-hidden="true"
      style={{ display: "block", width: "100%", maxWidth: 420, height: "auto" }}
    >
      {FLOWS.map((flow, i) => (
        <line
          key={i}
          {...flow}
          stroke="rgba(var(--ink-rgb),.22)"
          strokeWidth={1}
        />
      ))}

      {DIAGRAM_NODES.map((n) => (
        <DiagramNode
          key={n.label}
          x={n.cx}
          y={TOP_Y}
          w={TOP_W}
          label={n.label}
          color={n.color}
        />
      ))}

      <DiagramNode x={160} y={128} w={124} label="HiveMind agents" color={LAYER_ACCENT} emphasis />
      <DiagramNode x={160} y={212} w={176} label="Corporate Memory" color={LAYER_ACCENT} emphasis />
    </svg>
  );
}

/* ─────────────────────── The three pillars ─────────────────────── */

/**
 * A pillar as a full-width band: identity on the left, inventory on the right.
 * The identity column sticks while its capabilities scroll past it, so the
 * reader never loses which pillar a line belongs to. The band is separated
 * from the one above it by a rule in the pillar's own accent, so the three are
 * distinguishable at a glance while scrolling.
 */
function PillarBand({ pillar }: { pillar: Pillar }) {
  const Icon = pillar.icon;
  const rgb = hexToRgb(pillar.color);

  return (
    <section
      id={pillar.id}
      tabIndex={-1}
      aria-labelledby={`${pillar.id}-title`}
      style={{
        position: "relative",
        scrollMarginTop: SCROLL_MARGIN,
        borderTop: `1px solid rgba(${rgb}, .5)`,
        padding: "46px 0 34px",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
          gap: "28px 64px",
          alignItems: "start",
        }}
      >
        <div data-rv="" style={{ position: "sticky", top: 100 }}>
          <span
            aria-hidden="true"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 44,
              height: 44,
              borderRadius: 14,
              marginBottom: 20,
              background: `rgba(${rgb}, .12)`,
              color: pillar.color,
            }}
          >
            <Icon size={20} />
          </span>
          <h3
            id={`${pillar.id}-title`}
            style={{
              margin: 0,
              fontFamily: SANS,
              fontWeight: 700,
              fontSize: "clamp(22px, 2.4vw, 32px)",
              letterSpacing: "-0.02em",
              lineHeight: 1.16,
            }}
          >
            {pillar.label}
          </h3>
          {/* Under the heading, never above it. */}
          <p
            style={{
              margin: "10px 0 0",
              fontFamily: MONO,
              fontSize: 12,
              letterSpacing: ".04em",
              color: "rgba(var(--ink-rgb),.62)",
            }}
          >
            {pillar.product}
          </p>
          <p style={{ ...BODY, marginTop: 16, maxWidth: "42ch", lineHeight: 1.7 }}>
            {pillar.tagline}
          </p>
        </div>

        <ul
          data-rv=""
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 230px), 1fr))",
            columnGap: 40,
          }}
        >
          {pillar.items.map((item) => (
            <li
              key={item}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "13px 0",
                borderTop: HAIRLINE,
              }}
            >
              {/* A drawn mark rather than a check icon: the six lists on this
                  page are inventories, not feature comparisons, and a column
                  of ticks reads as a claim that each line is an advantage over
                  something. */}
              <span
                aria-hidden="true"
                style={{
                  width: 6,
                  height: 6,
                  flex: "none",
                  background: pillar.color,
                  transform: "rotate(45deg)",
                }}
              />
              <span style={{ ...CARD_BODY, lineHeight: 1.45 }}>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ──────────────────── HiveMind & Corporate Memory ──────────────────── */

/**
 * A layer, composed unlike a pillar on purpose: no sticky column, no split
 * inventory, a left rail instead of ruled rows. These two are not a fourth and
 * fifth pillar and should not look like one.
 */
function LayerBlock({ layer }: { layer: (typeof LAYERS)[number] }) {
  const Icon = layer.icon;
  const rgb = hexToRgb(LAYER_ACCENT);

  return (
    <article
      data-rv=""
      className="dc-h-card"
      style={{
        background: "var(--paper2)",
        border: HAIRLINE,
        borderRadius: 18,
        padding: 28,
        transition: "transform .25s, box-shadow .25s, border-color .25s",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <span
          aria-hidden="true"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 40,
            height: 40,
            flex: "none",
            borderRadius: 14,
            background: `rgba(${rgb}, .12)`,
            color: LAYER_ACCENT,
          }}
        >
          <Icon size={19} />
        </span>
        <h3 style={{ ...H3, fontSize: 20 }}>{layer.label}</h3>
      </div>

      <p
        style={{
          margin: "18px 0 0",
          fontFamily: MONO,
          fontSize: 12,
          letterSpacing: ".04em",
          lineHeight: 1.6,
          color: "rgba(var(--ink-rgb),.62)",
        }}
      >
        {layer.product}
      </p>
      <p style={{ ...CARD_BODY, marginTop: 10, maxWidth: "62ch", lineHeight: 1.7 }}>
        {layer.tagline}
      </p>

      <ul
        style={{
          listStyle: "none",
          margin: "22px 0 0",
          padding: "2px 0 2px 20px",
          display: "grid",
          gap: 9,
          borderLeft: `1px solid rgba(${rgb}, .35)`,
        }}
      >
        {layer.items.map((item) => (
          <li key={item} style={{ ...CARD_BODY, lineHeight: 1.45 }}>
            {item}
          </li>
        ))}
      </ul>
    </article>
  );
}

/* ─────────────────────── The pillar section ─────────────────────── */

function PillarsSection() {
  return (
    <section className="site-gutter" style={SECTION} aria-labelledby="pillars-heading">
      <div style={INNER}>
        <div data-rv="" style={{ maxWidth: 760, marginBottom: 56 }}>
          <Mark />
          <h2 id="pillars-heading" style={{ ...H2, marginBottom: 18 }}>
            People, finance, and the work in between.
          </h2>
          <p style={{ ...BODY, maxWidth: "62ch", lineHeight: 1.7 }}>
            Three pillars, not a suite of separate products. The same three the
            platform itself is built around, listed here in full.
          </p>
        </div>

        {PILLARS.map((pillar) => (
          <PillarBand key={pillar.id} pillar={pillar} />
        ))}
      </div>
    </section>
  );
}

/* ─────────────────── The layer across all three ─────────────────── */

function IntelligenceSection() {
  return (
    <section
      id="intelligence"
      tabIndex={-1}
      aria-labelledby="intelligence-heading"
      className="site-gutter"
      style={{ ...SECTION, scrollMarginTop: SCROLL_MARGIN }}
    >
      <div style={INNER}>
        <div data-rv="" style={{ maxWidth: 760, marginBottom: 56 }}>
          <Mark />
          <h2 id="intelligence-heading" style={{ ...H2, marginBottom: 18 }}>
            One layer, running through all three.
          </h2>
          <p style={{ ...BODY, maxWidth: "62ch", lineHeight: 1.7 }}>
            HiveMind and Corporate Memory are not a fourth and fifth product.
            They sit across people, finance, and operations, acting on the work
            as it happens, and keeping what the organization learns from it.
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))",
            gap: "48px 72px",
            alignItems: "start",
          }}
        >
          <div data-rv="" style={{ position: "sticky", top: 100 }}>
            <ConvergenceDiagram />
            <p
              style={{
                margin: "26px 0 0",
                maxWidth: "46ch",
                fontSize: 14,
                lineHeight: 1.7,
                color: "rgba(var(--ink-rgb),.62)",
              }}
            >
              Work happens in the three pillars, agents act on it, and what the
              organization learns from that activity accumulates underneath.
            </p>
          </div>

          <div style={{ display: "grid", gap: 20 }}>
            {LAYERS.map((layer) => (
              <LayerBlock key={layer.label} layer={layer} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────── The foundation ─────────────────────────── */

function FoundationSection() {
  return (
    <section
      id="foundation"
      tabIndex={-1}
      aria-labelledby="foundation-heading"
      className="site-gutter"
      style={{ ...SECTION, scrollMarginTop: SCROLL_MARGIN }}
    >
      <div
        style={{
          ...INNER,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
          gap: "40px 64px",
          alignItems: "center",
        }}
      >
        {/* Subordinate by design: this is what the three pillars stand on,
            not a fourth thing to evaluate. */}
        <div data-rv="">
          <Mark />
          <h2
            id="foundation-heading"
            style={{
              margin: 0,
              fontFamily: SANS,
              fontWeight: 700,
              fontSize: "clamp(24px, 2.6vw, 34px)",
              letterSpacing: "-0.02em",
              lineHeight: 1.16,
            }}
          >
            What it all runs on.
          </h2>
          <p style={{ ...BODY, marginTop: 16, maxWidth: "42ch", lineHeight: 1.7 }}>
            The same platform underneath every capability above.
          </p>
        </div>

        {/* A plinth, not cards: one bordered slab of stacked rows, so it
            reads as a single base rather than five more things to weigh up.
            Deliberately one column at every width — a responsive grid here
            leaves an empty cell on the last row, and an empty cell in a
            slab looks like a bug rather than a layout. */}
        <ul
          data-rv=""
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            overflow: "hidden",
            background: "var(--paper2)",
            border: HAIRLINE,
            borderRadius: 18,
          }}
        >
          {FOUNDATION.map((item, i) => (
            <li
              key={item}
              style={{
                padding: "17px 26px",
                fontSize: 14.5,
                lineHeight: 1.45,
                color: "rgba(var(--ink-rgb),.66)",
                borderTop: i === 0 ? undefined : HAIRLINE,
              }}
            >
              {item}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ─────────────────────────── Close ─────────────────────────── */

/**
 * What used to sit here was "Our Process: Discover / Design / Deliver", with
 * "assess opportunities, risks, and ROI" under the first step. That is how an
 * agency describes an engagement, not how a product describes itself, so it is
 * gone rather than reworded.
 */
function CloseSection() {
  return (
    <section
      className="dc-seam dc-seam-foot"
      style={{
        position: "relative",
        overflow: "clip",
        background: "var(--band)",
        color: "var(--band-ink)",
      }}
      aria-labelledby="solutions-close"
    >
      {/* Not parallaxed: it is anchored to the section's midpoint. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "50%",
          right: -220,
          width: 640,
          height: 640,
          marginTop: -320,
          border: "1px solid rgba(var(--band-acc-rgb),.18)",
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />

      <div
        className="site-gutter"
        style={{
          maxWidth: 1240,
          margin: "0 auto",
          paddingTop: 128,
          paddingBottom: 110,
          position: "relative",
          textAlign: "center",
        }}
      >
        <h2
          data-rv=""
          id="solutions-close"
          style={{
            margin: "0 0 18px",
            fontFamily: SANS,
            fontWeight: 700,
            fontSize: "clamp(34px, 4.4vw, 60px)",
            letterSpacing: "-0.02em",
            lineHeight: 1.08,
          }}
        >
          See it on your own operations.
        </h2>

        <p
          data-rv=""
          style={{
            margin: "0 auto 36px",
            maxWidth: "56ch",
            fontSize: 17,
            color: "rgba(var(--band-ink-rgb),.7)",
          }}
        >
          A walkthrough of the capabilities above, built around how your teams
          actually work.
        </p>

        {/* Pricing leads here, which is the one place on the site where it
            should: a visitor who has read the whole capability list already
            knows what it does and is asking what it costs.

            The demo stays as the secondary rather than disappearing, because
            the heading above promises a walkthrough of these capabilities. A
            page that says "see it on your own operations" and offers only a
            price list answers a question it did not ask. Same pair as the
            homepage's closing call, in the opposite order, for the same
            reason: that page closes on the pitch, this one on the evidence. */}
        <div
          data-rv=""
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          <Link
            to="/pricing"
            className="dc-h-btn-cyan"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              background: "rgb(var(--band-acc-rgb))",
              color: "var(--band-on-acc)",
              padding: "16px 30px",
              borderRadius: 999,
              fontWeight: 700,
              fontSize: 15.5,
              transition: "transform .2s, box-shadow .2s",
            }}
          >
            See pricing
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
          <Link
            to="/contact"
            className="dc-h-ghost-light"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              border: "1px solid rgba(var(--band-ink-rgb),.3)",
              color: "var(--band-ink)",
              padding: "16px 30px",
              borderRadius: 999,
              fontWeight: 600,
              fontSize: 15.5,
              transition: "border-color .2s, background .2s",
            }}
          >
            Book a demo
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────── Page ─────────────────────────── */

export default function Solutions() {
  return (
    <DcPage>
      <Hero />

      {/* ── The portals ──
          Ported from the homepage on `main`. It is a visual overview of the
          nine portals; the three pillar sections below expand on the same
          ground in detail, so this runs first and they follow. */}
      <PortalCarousel />

      <PillarsSection />
      <IntelligenceSection />
      <FoundationSection />
      <CloseSection />
    </DcPage>
  );
}
